import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: string | HTMLElement, params: {
        sitekey: string;
        hl?: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => number;
      reset: (widgetId: number) => void;
      getResponse: (widgetId: number) => string;
      execute: (sitekey: string, options: { action: string }) => Promise<string>;
      ready: (callback: () => void) => void;
    };
    onRecaptchaLoad?: () => void;
  }
}

interface RecaptchaProps {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  sitekey?: string;
  language?: string;
}

const SUPPORTED_LANGUAGES = ['ru', 'en', 'be', 'kk', 'uk', 'de', 'fr', 'es', 'pt', 'it', 'nl', 'pl', 'tr', 'zh', 'ja'];
const SAFE_LANGUAGE = (lang: string): string => SUPPORTED_LANGUAGES.includes(lang) ? lang : 'ru';

const Recaptcha: React.FC<RecaptchaProps> = ({
  onVerify,
  onError,
  onExpire,
  sitekey,
  language = 'ru',
}) => {
  const safeLanguage = SAFE_LANGUAGE(language);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const effectiveSitekey = sitekey || import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!effectiveSitekey) {
    console.error('Recaptcha: VITE_RECAPTCHA_SITE_KEY is not configured');
  }

  useEffect(() => {
    if (!effectiveSitekey) {
      setError('reCAPTCHA site key is not configured');
      setLoading(false);
      return;
    }

    if (window.grecaptcha) {
      window.grecaptcha.ready(() => renderWidget());
      return;
    }

    window.onRecaptchaLoad = () => {
      renderWidget();
    };

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=${safeLanguage}`;
    script.async = true;
    script.defer = true;

    script.onerror = () => {
      const msg = 'Failed to load reCAPTCHA script';
      console.error(msg);
      setError(msg);
      setLoading(false);
      if (onError) onError(msg);
    };

    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch { /* ignore */ }
      }
    };
  }, [safeLanguage]);

  const renderWidget = () => {
    if (!window.grecaptcha || !containerRef.current) {
      const msg = 'reCAPTCHA not available';
      setError(msg);
      setLoading(false);
      if (onError) onError(msg);
      return;
    }

    try {
      const id = window.grecaptcha.render(containerRef.current, {
        sitekey: effectiveSitekey,
        hl: language,
        callback: (token: string) => {
          setVerified(true);
          setLoading(false);
          onVerify(token);
        },
        'expired-callback': () => {
          setVerified(false);
          if (onExpire) onExpire();
        },
        'error-callback': () => {
          setError('reCAPTCHA verification error');
          setLoading(false);
          if (onError) onError('recaptcha_error');
        },
      });
      widgetIdRef.current = id;
      setLoading(false);
    } catch (e) {
      const msg = 'Failed to render reCAPTCHA';
      console.error(msg, e);
      setError(msg);
      setLoading(false);
      if (onError) onError(msg);
    }
  };

  const handleRetry = () => {
    setError(null);
    setVerified(false);
    setLoading(true);

    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
      setLoading(false);
    } else {
      renderWidget();
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, position: 'relative', minHeight: '78px' }}>
      <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center' }} />

      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper' }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2" color="textSecondary">
            Loading captcha...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetry} sx={{ mt: 1 }}>
              Retry
            </Button>
          }
          sx={{ mb: 1 }}
        >
          <Typography variant="body2">Error loading reCAPTCHA: {error}</Typography>
        </Alert>
      )}
    </Box>
  );
};

export default Recaptcha;
